import { useState } from "react"

import {
  AiHelpArticle,
  getAiHelpArticles,
  getAiHelpGaps,
  resolveAiHelpGaps
} from "@domain/ai_help"
import useTranslate from "@hooks/use_translate"
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import dayjs from "dayjs"

import { AI_HELP_ARTICLES_KEY } from "./ArticlesTab"

/** Questions the Operator found no help content for: the list of articles to write next. */
export default function GapsTab() {
  const { translate } = useTranslate()
  const queryClient = useQueryClient()
  const [resolved, setResolved] = useState(false)
  const [selected, setSelected] = useState<string[]>([])
  const [article, setArticle] = useState<AiHelpArticle | null>(null)
  const [saving, setSaving] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ["ai-help-gaps", resolved],
    queryFn: async () => {
      const response = await getAiHelpGaps(resolved)
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage || "failed")
      }
      return response.data
    }
  })
  const { data: articles = [] } = useQuery({
    queryKey: AI_HELP_ARTICLES_KEY,
    queryFn: async () => {
      const response = await getAiHelpArticles()
      return response.data ?? []
    }
  })
  const titles = new Map(articles.map((item) => [item._id, item.title]))

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    )

  const resolve = async () => {
    setSaving(true)
    try {
      const response = await resolveAiHelpGaps({
        ids: selected,
        ...(article && { articleId: article._id })
      })
      if (!response.isSuccess) {
        showErrorToast(response.errorMessage || translate("ai_help_failed"))
        return
      }
      showSuccessToast(translate("ai_help_gaps_resolved"))
      setSelected([])
      setArticle(null)
      await queryClient.invalidateQueries({ queryKey: ["ai-help-gaps"] })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap={1}>
      <Stack direction="row" gap={2} alignItems="center">
        <Typography variant="body2" color="text.secondary" flex={1}>
          {translate("ai_help_gaps_desc")}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={resolved}
          onChange={(_event, value: boolean | null) => {
            if (value !== null) {
              setResolved(value)
              setSelected([])
            }
          }}
        >
          <ToggleButton value={false}>
            {translate("ai_help_gaps_open")}
          </ToggleButton>
          <ToggleButton value>{translate("ai_help_gaps_closed")}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {!resolved && selected.length > 0 && (
        <Stack direction="row" gap={1} alignItems="center">
          <Autocomplete
            size="small"
            sx={{ minWidth: 320 }}
            options={articles}
            value={article}
            onChange={(_event, value) => setArticle(value)}
            getOptionLabel={(option) => option.title}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={translate("ai_help_gaps_answered_by")}
              />
            )}
          />
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => void resolve()}
          >
            {translate("ai_help_gaps_resolve").replace(
              "{count}",
              String(selected.length)
            )}
          </Button>
        </Stack>
      )}

      {isLoading && <CircularProgress sx={{ alignSelf: "center" }} />}
      {(error || (!isLoading && !data)) && (
        <Alert severity="error">{translate("ai_help_load_failed")}</Alert>
      )}
      {data && (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {!resolved && <TableCell padding="checkbox" />}
                <TableCell>{translate("ai_help_col_question")}</TableCell>
                <TableCell>{translate("ai_help_col_customer")}</TableCell>
                <TableCell>{translate("ai_help_col_asked_at")}</TableCell>
                {resolved && (
                  <TableCell>{translate("ai_help_col_answered_by")}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      py={2}
                      textAlign="center"
                    >
                      {translate("ai_help_gaps_empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {data.map((gap) => (
                <TableRow key={gap._id}>
                  {!resolved && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.includes(gap._id)}
                        onChange={() => toggle(gap._id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>{gap.question}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{gap.customer}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {dayjs(gap.createdAt).format("DD.MM.YYYY HH:mm")}
                    </Typography>
                  </TableCell>
                  {resolved && (
                    <TableCell>
                      <Typography variant="caption">
                        {(gap.article && titles.get(gap.article)) || "—"}
                      </Typography>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  )
}
